<template>
  <div class="v2-order-detail">
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading order..."
      />
    </div>

    <template v-else-if="order">
      <!-- Header -->
      <div class="v2-order-detail__header">
        <div class="v2-order-detail__header-left">
          <button
            v-if="!shipmentOrder || shipmentOrder.shipmentId"
            class="v2-order-detail__back-btn"
            @click="goBack"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="19"
                y1="12"
                x2="5"
                y2="12"
              />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <h1 class="v2-heading-sm v2-m-0">
            {{ order.shipmentSeqId ? `Shipment ID: ${order.shipmentSeqId}` : `Order ID: ${order.orderSeqId}` }}
          </h1>
        </div>
        <div class="v2-order-detail__header-actions">
          <!-- Download -->
          <v2-button
            v-if="!isShipment && canShowDownloadOrderButton"
            variant="outline"
            @click="showDownloadModal = true"
          >
            Download Order
          </v2-button>
          <!-- Import/Export for shipment creation -->
          <v2-button
            v-if="shipmentOrder && !order.shipmentId"
            variant="outline"
            @click="$emit('open-import-export')"
          >
            Import/Export
          </v2-button>
          <!-- Shipment action -->
          <v2-shipment-action
            v-if="isShipment && (!shipmentOrder || shipmentOrder.shipmentId)"
            :shipment="order"
            @download-shipment="showDownloadModal = true"
            @load-shipment="$emit('load-shipment')"
          />
          <!-- Upload Invoice (brand only, confirmed) -->
          <v2-order-upload-invoice
            v-if="hasOrderConfirmed && !order.isModifyEnabledBrand && isBrand"
            :order="order"
            :load-order="loadOrder"
          />
          <!-- Back to Received -->
          <v2-button
            v-if="canShowBackToReceivedButton"
            variant="outline"
            :disabled="isRevertingOrder"
            @click="handleRevertToAccepted"
          >
            {{ isRevertingOrder ? 'Loading...' : 'Back to Received' }}
          </v2-button>
          <!-- Duplicate Order -->
          <v2-duplicate-order-button
            v-if="canShowDuplicateOrderButton"
            :order="order"
          />
          <!-- Cancel order (brand, confirmed, self-managed) -->
          <v2-button
            v-if="showBrandCancelButton"
            variant="destructive"
            @click="cancelOrderByBrand(false)"
          >
            Cancel order
          </v2-button>
          <!-- Order actions -->
          <v2-order-actions
            v-if="!showBrandCancelButton"
            ref="orderActionsRef"
            :order="order"
            :original-order="orderData"
            :total-units="totalUnits"
            :modify-using-import="modifyUsingImport"
            :is-direct-brand="isDirectBrand"
            :load-order="loadOrder"
            :is-modify-enabled-prop="isModifyEnabled"
            :is-in-draft="isInDraft"
            :imported-product-statuses="importedProductStatuses"
            :selected-product-ids="partialConfirmProductIds"
            @enable-modify-changes="enableModifyProducts"
            @revert-modified-changes="revertModifiedChanges"
            @update-order="updateOrder"
            @export-order-for-modify="exportOrderForModify"
            @open-import-modal="showImportForModifyModal = true"
          />
          <!-- Shipment creation buttons -->
          <template v-if="shipmentOrder && !order.shipmentId">
            <v2-button
              variant="outline"
              @click="$emit('cancel-create-shipment')"
            >
              Cancel
            </v2-button>
            <v2-button
              variant="primary"
              :disabled="isShipping"
              @click="$emit('create-shipment')"
            >
              {{ isShipping ? 'Loading...' : 'Create Shipment' }}
            </v2-button>
          </template>
        </div>
      </div>

      <!-- Info Grid: Logs | Summary | Addresses -->
      <div class="v2-order-detail__info-grid">
        <v2-order-logs :order="order" />
        <v2-order-summary
          :order="order"
          @load-order="loadOrder($route.params.orderId)"
        />
        <v2-order-addresses :order="order" />
      </div>

      <!-- Products filter -->
      <div
        v-if="order.products && order.products.length"
        class="v2-order-detail__filter"
      >
        <v2-order-products-filter
          :order-products="order.products"
          @on-apply-filter="onAppliedFilter"
        />
      </div>

      <!-- Products -->
      <v2-order-products
        :key="reloadkey"
        :order="order"
        :order-products="isProductsFilterApplied ? filteredOrderProducts : order.products"
        :is-shipment-view="isShipment"
        :is-shipment-settings-modal="!!shipmentOrder"
        :is-modify-enabled-prop="isModifyEnabled"
        :is-filter-applied="isProductsFilterApplied"
        :is-return-to-order-enabled="isReturnToOrderEnabled"
        @remove-product="removeOrderProduct"
        @ship-now="handleShipNow"
        @reload-order="loadOrder($route.params.orderId)"
        @load-shipment="$emit('load-shipment')"
        @partial-confirm-selection="partialConfirmProductIds = $event"
      />

      <!-- Download modal -->
      <v2-modal
        :show="showDownloadModal"
        title="Download Order"
        size="sm"
        @close="showDownloadModal = false"
      >
        <div class="v2-order-detail__download-options">
          <button
            v-for="option in downloadOptions"
            :key="option.value"
            class="v2-order-detail__download-option"
            :disabled="isDownloading"
            @click="handleDownload(option)"
          >
            {{ option.text }}
          </button>
        </div>
        <div
          v-if="isDownloading"
          class="v2-flex v2-justify-center v2-mt-4"
        >
          <v2-spinner label="Downloading..." />
        </div>
      </v2-modal>

      <!-- Brand cancel order modal -->
      <v2-modal
        :show="showBrandCancelModal"
        title="Cancel order"
        @close="showBrandCancelModal = false"
      >
        <p>This order contains shipments.</p>
        <ul v-if="cancellingShipments.length">
          <li
            v-for="(item, index) in cancellingShipments"
            :key="index"
          >
            {{ item.shipmentSeqId }} - {{ item.status }}
          </li>
        </ul>
        <p>Proceed with the cancellation?</p>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showBrandCancelModal = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="destructive"
            @click="cancelOrderByBrand(true); showBrandCancelModal = false"
          >
            Confirm
          </v2-button>
        </template>
      </v2-modal>

      <!-- Revert order modal -->
      <v2-modal
        :show="showRevertModal"
        title="Revert Order Status"
        @close="showRevertModal = false"
      >
        <p>
          Are you sure you want to revert this order to Order Received?
          This will restore the product quantities back to your inventory.
        </p>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showRevertModal = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="destructive"
            @click="confirmRevertOrder"
          >
            Confirm
          </v2-button>
        </template>
      </v2-modal>

      <!-- Unsaved changes modal -->
      <v2-modal
        :show="showUnsavedModal"
        title="Unsaved Changes"
        @close="cancelLeave"
      >
        <p>Do you really want to leave? Your unsaved changes will be lost.</p>
        <template #footer>
          <v2-button
            variant="outline"
            @click="cancelLeave"
          >
            Stay
          </v2-button>
          <v2-button
            variant="destructive"
            @click="confirmLeave"
          >
            Leave
          </v2-button>
        </template>
      </v2-modal>

      <!-- Upload Order modal -->
      <v2-modal
        :show="showUploadOrderModal"
        title="Upload Order"
        @close="showUploadOrderModal = false"
      >
        <div>
          <label class="v2-order-detail__upload-label">
            Upload the Order products file
          </label>
          <input
            type="file"
            accept=".xlsx"
            class="v2-order-detail__file-input"
            @change="handleUploadFileChange"
          >
        </div>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showUploadOrderModal = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="primary"
            :disabled="!orderUploadFile || uploading"
            @click="handleSyncOrderProducts"
          >
            {{ uploading ? 'Uploading...' : 'Upload' }}
          </v2-button>
        </template>
      </v2-modal>

      <!-- Import Order for Modify modal -->
      <v2-modal
        :show="showImportForModifyModal"
        title="Import Order for Modify"
        @close="showImportForModifyModal = false"
      >
        <div>
          <label class="v2-order-detail__upload-label">
            Upload the modified order file (.xlsx)
          </label>
          <input
            type="file"
            accept=".xlsx"
            class="v2-order-detail__file-input"
            @change="handleImportFileChange"
          >
        </div>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showImportForModifyModal = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="primary"
            :disabled="!importForModifyFile || importingForModify"
            @click="handleImportForModifyAction"
          >
            {{ importingForModify ? 'Importing...' : 'Import' }}
          </v2-button>
        </template>
      </v2-modal>
    </template>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2ShipmentAction from '@/views/v2/shipments/V2ShipmentAction.vue'
import { useOrderDetail } from '@/composables/useOrderDetail'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { getOrderDownloadOptions } from '@/views/apps/order/order-download-options'
import V2OrderLogs from './V2OrderLogs.vue'
import V2OrderSummary from './V2OrderSummary.vue'
import V2OrderAddresses from './V2OrderAddresses.vue'
import V2OrderProducts from './V2OrderProducts.vue'
import V2OrderProductsFilter from './V2OrderProductsFilter.vue'
import V2OrderActions from './V2OrderActions.vue'
import V2DuplicateOrderButton from './V2DuplicateOrderButton.vue'
import V2OrderUploadInvoice from './V2OrderUploadInvoice.vue'

const { SHIPMENT_STATUS } = c

export default {
  name: 'V2OrderDetail',
  components: {
    V2Spinner,
    V2Button,
    V2Modal,
    V2OrderLogs,
    V2OrderSummary,
    V2OrderAddresses,
    V2OrderProducts,
    V2OrderProductsFilter,
    V2OrderActions,
    V2DuplicateOrderButton,
    V2OrderUploadInvoice,
    V2ShipmentAction,
  },
  mixins: [UserRoleMixin],
  props: {
    shipmentOrder: {
      type: Object,
      default: null,
    },
    isShipping: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const shipmentOrderRef = ref(props.shipmentOrder)
    const detail = useOrderDetail(shipmentOrderRef)
    const {
      cancelOrderByBrand,
      handleRevertToAccepted,
      ...rest
    } = detail
    return {
      ...rest,
      cancelOrderByBrandAction: cancelOrderByBrand,
      revertToAcceptedAction: handleRevertToAccepted,
    }
  },
  data() {
    return {
      showDownloadModal: false,
      showBrandCancelModal: false,
      showRevertModal: false,
      showUnsavedModal: false,
      showUploadOrderModal: false,
      showImportForModifyModal: false,
      cancellingShipments: [],
      pendingRoute: null,
      removeRouterGuard: null,
      isNavigationConfirmed: false,
      // KP-4363: products ticked for partial confirmation
      partialConfirmProductIds: [],
    }
  },
  computed: {
    isReturnToOrderEnabled() {
      return this.isBrand
        && this.isShipment
        && this.order?.status === SHIPMENT_STATUS.SHIPMENT_CREATED
    },
    canShowDuplicateOrderButton() {
      return (
        !this.shipmentOrder
        && !this.order.shipmentId
        && !this.isShipment
        && this.hasDuplicateOrderFeature
      )
    },
    showBrandCancelButton() {
      if (!this.order) return false
      return (
        this.order.status === this.ORDER_STATUS?.CONFIRMED
        && this.order.isSelfManagedOrder
        && !this.shipmentOrder
        && this.isBrand
      )
    },
    hasUnsavedChanges() {
      return this.modifyUsingImport || this.isModifyEnabled
    },
    shouldShowExpectedDatePopup() {
      if (!this.orderData) return false
      return (
        this.orderData.shouldDisplayExpectedAvailabilityDatePopup
        && this.orderData.expectedAvailabilityDate == null
        && !this.isDirectBrand
      )
    },
    downloadOptions() {
      return getOrderDownloadOptions({
        order: this.order,
        isShipment: this.isShipment,
        isShipmentOrder: !!this.shipmentOrder,
        canDownloadInvoice: this.canShowDownloadOrderInvoiceBtn,
      })
    },
  },
  watch: {
    hasUnsavedChanges(hasChanges) {
      if (hasChanges) {
        window.addEventListener('beforeunload', this.handleBeforeUnload)
      } else {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
      }
    },
  },
  async mounted() {
    this.setupRouterGuard()
    await this.loadOrder(this.$route.params.orderId)
    this.initDownloadOptions()
    this.scheduleExpectedDatePopup()
  },
  beforeDestroy() {
    if (this.removeRouterGuard) {
      this.removeRouterGuard()
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    setupRouterGuard() {
      this.removeRouterGuard = this.$router.beforeEach((to, from, next) => {
        if (this.hasUnsavedChanges && !this.isNavigationConfirmed) {
          this.pendingRoute = to
          this.showUnsavedModal = true
          return next(false)
        }
        this.isNavigationConfirmed = false
        next()
      })
    },
    scheduleExpectedDatePopup() {
      if (!this.shouldShowExpectedDatePopup) return
      setTimeout(() => {
        if (this.$refs.orderActionsRef) {
          this.$refs.orderActionsRef.openDateModal()
        }
      }, 1000)
    },
    goBack() {
      if (this.isShipment) {
        this.$router.push({ name: 'v2-shipments' })
      } else {
        this.$router.push({ name: 'v2-orders' })
      }
    },
    handleBeforeUnload(e) {
      e.preventDefault()
      e.returnValue = ''
    },
    confirmLeave() {
      this.isNavigationConfirmed = true
      this.showUnsavedModal = false
      this.$router.push(this.pendingRoute)
    },
    cancelLeave() {
      this.pendingRoute = null
      this.showUnsavedModal = false
    },
    handleShipNow(orderData) {
      this.$emit('ship-now', orderData, this.orderData?.products?.length || 0)
    },
    handleDownload(option) {
      this.downloadOrderProducts(option, this.$route.params.orderId)
      this.showDownloadModal = false
    },
    async handleRevertToAccepted() {
      const result = await this.revertToAcceptedAction(this.$route.params.orderId)
      if (result?.showRevertModal) {
        this.showRevertModal = true
      }
    },
    async confirmRevertOrder() {
      this.showRevertModal = false
      await this.revertOrderToAccepted(this.$route.params.orderId)
    },
    cancelOrderByBrand(isConfirmed) {
      this.cancelOrderByBrandAction(isConfirmed, this.$route.params.orderId)
        .then(result => {
          if (result?.showShipmentsModal) {
            this.cancellingShipments = result.cancellingShipments
            this.showBrandCancelModal = true
          }
        })
    },
    handleUploadFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.orderUploadFile = file
      }
    },
    handleImportFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.importForModifyFile = file
      }
    },
    async handleSyncOrderProducts() {
      const result = await this.syncOrderProducts()
      if (result?.success) {
        this.showUploadOrderModal = false
      }
    },
    async handleImportForModifyAction() {
      const result = await this.handleImportForModify()
      if (result?.success) {
        this.showImportForModifyModal = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-order-detail {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__back-btn {
    background: none;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    padding: 0.375rem;
    cursor: pointer;
    color: var(--v2-foreground);
    display: flex;
    align-items: center;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__filter {
    margin-bottom: 1rem;
    position: sticky;
    top: 0;
    z-index: 50;
    // True glassmorphic — single sticky bar, GPU cost is bounded (one
    // re-blur per scroll frame for one element, not multiplied). Using
    // `backdrop-filter` here gives the live frosted-glass cue (page
    // content underneath gets blurred as it scrolls past the bar).
    background: var(--v2-glass-surface-1);
    backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
    -webkit-backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
    border: 1px solid var(--v2-glass-stroke);
    box-shadow:
      0 1px 0 0 oklch(1 0 0 / 0.1) inset,
      var(--v2-glass-shadow);
    padding: 0.5rem;
    border-radius: var(--v2-radius-md);
  }

  &__download-options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__download-option {
    @include v2-glass-card-flat;
    display: block;
    width: 100%;
    padding: 0.75rem 1rem;
    text-align: left;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      border-color: var(--v2-glass-stroke-strong);
      background: var(--v2-glass-sheen), var(--v2-glass-surface-1);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__upload-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: 0.5rem;
  }

  &__file-input {
    display: block;
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-background);

    &::file-selector-button {
      padding: 0.375rem 0.75rem;
      margin-right: 0.75rem;
      border: 1px solid var(--v2-border);
      border-radius: 0.375rem;
      background: var(--v2-muted);
      color: var(--v2-foreground);
      font-size: 0.8125rem;
      cursor: pointer;

      &:hover {
        background: var(--v2-border);
      }
    }
  }
}
</style>
